import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Trash2, RotateCcw, Flame, Search } from "lucide-react";

export default function AdminPatients() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [includeDeleted, setIncludeDeleted] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get("/patients", { params: { include_deleted: includeDeleted, q: q || undefined } });
      setRows(data);
    } catch { toast.error("Load failed"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q, includeDeleted]);

  const soft = async (p) => {
    if (!window.confirm(`Move ${p.name} (${p.hospital_id}) to Deleted? Data is preserved — you can restore later.`)) return;
    try { await api.delete(`/patients/${p.id}`); toast.success("Moved to deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const restore = async (p) => {
    try { await api.post(`/patients/${p.id}/restore`); toast.success("Restored"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const purge = async (p) => {
    if (!window.confirm(`PERMANENTLY delete ${p.name} (${p.hospital_id}) and every clinical record (vitals, meds, notes, investigations, timeline, discharge)? This CANNOT be undone.`)) return;
    if (!window.confirm(`Type-safety: really delete ${p.hospital_id}?`)) return;
    try {
      const { data } = await api.delete(`/patients/${p.id}/purge`);
      const total = Object.values(data.cascade || {}).reduce((a, b) => a + b, 0);
      toast.success(`Purged — ${total} clinical rows removed`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-4" data-testid="admin-patients">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Search name / ID / bed / consultant" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 w-full md:w-96" />
        </div>
        <label className="text-sm inline-flex items-center gap-2">
          <input type="checkbox" checked={includeDeleted} onChange={(e) => setIncludeDeleted(e.target.checked)} className="h-4 w-4 accent-primary" />
          Show soft-deleted
        </label>
      </div>

      <div className="card-clinical overflow-hidden">
        <div className="grid grid-cols-12 px-4 py-2 border-b border-border text-[11px] uppercase tracking-wider text-muted-foreground">
          <div className="col-span-2">Hospital ID</div>
          <div className="col-span-3">Name</div>
          <div className="col-span-2">Consultant</div>
          <div className="col-span-1">Bed</div>
          <div className="col-span-2">Status</div>
          <div className="col-span-2 text-right">Actions</div>
        </div>
        {rows.length === 0 && <div className="p-6 text-center text-sm text-muted-foreground">No patients</div>}
        {rows.map((p) => (
          <div key={p.id} className={`grid grid-cols-12 px-4 py-3 border-b border-border/60 items-center text-sm ${p.deleted ? "opacity-60" : ""}`}>
            <div className="col-span-2 font-mono text-xs">{p.hospital_id}</div>
            <div className="col-span-3 font-medium truncate">
              {p.name}
              {p.demo && <Badge variant="outline" className="ml-2 rounded-full text-[10px]">DEMO</Badge>}
              {p.critical && <Badge variant="destructive" className="ml-2 rounded-full text-[10px]">CRITICAL</Badge>}
            </div>
            <div className="col-span-2 truncate text-muted-foreground">{p.consultant || "—"}</div>
            <div className="col-span-1 truncate">{p.bed_number || "—"}</div>
            <div className="col-span-2">
              {p.deleted
                ? <span className="text-[10px] rounded-full px-2 py-0.5 bg-warn">deleted</span>
                : <span className={`text-[10px] rounded-full px-2 py-0.5 ${p.status === "admitted" ? "bg-vacant" : "bg-muted text-muted-foreground"}`}>{p.status || "—"}</span>}
            </div>
            <div className="col-span-2 flex justify-end gap-2">
              {p.deleted
                ? <Button size="sm" variant="outline" className="h-7 rounded-full text-xs" onClick={() => restore(p)} data-testid={`restore-${p.id}`}><RotateCcw size={12} className="mr-1" /> Restore</Button>
                : <Button size="sm" variant="outline" className="h-7 rounded-full text-xs" onClick={() => soft(p)} data-testid={`softdel-${p.id}`}><Trash2 size={12} className="mr-1" /> Delete</Button>}
              <Button size="sm" variant="outline" className="h-7 rounded-full text-xs text-red-600 hover:bg-red-500 hover:text-white border-red-500/40" onClick={() => purge(p)} data-testid={`purge-${p.id}`}>
                <Flame size={12} className="mr-1" /> Purge
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
